import { PageHeader } from "@components";
import { ROUTES } from "@config";
import {
  useGetDevice,
  useGetDocuments,
  useGetDocumentsTemplates,
  useGetTasks,
  useGetUser,
} from "@hooks";
import { AssistantProcess, AssistantProcessStatus } from "@models";
import { NoteAddOutlined } from "@mui/icons-material";
import { Alert, Breadcrumbs, Typography } from "@mui/material";
import { DocumentStatus } from "@types";
import { fillRouteWithSlugs } from "@utils";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ListCard } from "src/components/Assistant/ListCard";
import { LoadingDocList } from "src/components/Assistant/LoadingDocList";
import { NavigationButtons } from "src/components/Assistant/NavigationButtons";
import { NoDocsToUpdate } from "src/components/Assistant/NoDocsToUpdate";
import { DocWarnings } from "src/components/DocViewer/components/DocWarnings";
import { ROUTE_SLUGS } from "src/config/navigation/routes";
import { useGetAssistantProcesses } from "src/hooks/queries/assistant-process";
import { useUpdateAssistantProcessMutation } from "src/hooks/queries/assistant-process-mutations";
import {
  getDocTypeStatus,
  getIncompleteDependenciesRecursively,
} from "src/utils/documents";

export const DU_ListOfDocsToChange = () => {
  const { data: user } = useGetUser();
  const { orgId = "", deviceId = "" } = useParams();
  const { data: templatesConfig } = useGetDocumentsTemplates({
    orgId,
    deviceId,
  });
  const { data: documents } = useGetDocuments(orgId, deviceId);
  const { data: device } = useGetDevice({ orgId, deviceId });
  const { data: assistantProcesses } = useGetAssistantProcesses({
    orgId,
    deviceId,
    status: AssistantProcessStatus.IN_PROGRESS,
  });
  const { mutate: updateAssistantProcess } = useUpdateAssistantProcessMutation(
    orgId,
    deviceId
  );
  const { data: tasks } = useGetTasks(orgId, deviceId);

  const selectedAssistantProcess = assistantProcesses?.[0];

  const navigate = useNavigate();

  const [showLoading, setShowLoading] = useState(true);

  const handleNext = (assistantProcess: AssistantProcess) => {
    const updatedState: AssistantProcess["state"] = {
      ...assistantProcess.state,
      currentPath: ROUTES.ASSISTANT_DU_MULTI_STEP_FORM,
      changes: assistantProcess.state.changes.map((change) => ({
        ...change,
        dataKeyId: change.dataKeyId,
      })),
    };

    updateAssistantProcess({
      processId: assistantProcess.id,
      body: {
        state: updatedState,
      },
    });

    navigate(
      fillRouteWithSlugs(ROUTES.ASSISTANT_DU_MULTI_STEP_FORM, {
        [ROUTE_SLUGS.ORG_ID]: orgId,
        [ROUTE_SLUGS.DEVICE_ID]: deviceId,
      })
    );
  };

  // Show the loading screen for at least3 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowLoading(false);
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  if (
    showLoading ||
    !selectedAssistantProcess ||
    !documents ||
    !user ||
    !device ||
    !templatesConfig ||
    !tasks
  ) {
    return <LoadingDocList />;
  }

  const dataKeysToUpdate = selectedAssistantProcess.state.dataKeysToUpdate;

  if (!dataKeysToUpdate || dataKeysToUpdate.length === 0) {
    return <NoDocsToUpdate />;
  }

  let templateKeysToComplete: string[] = [];
  let incompleteDocsList: string[] = [];

  // Sort out incomplete documents & documents to show for update
  dataKeysToUpdate.forEach((dataKeyToUpdate) => {
    const docStatus = getDocTypeStatus({
      documents,
      templateKey: dataKeyToUpdate.templateKey,
      user,
      orgId,
    });

    templateKeysToComplete.push(dataKeyToUpdate.templateKey);

    const templateConfig = templatesConfig[dataKeyToUpdate.templateKey];

    if (
      docStatus === DocumentStatus.DRAFT &&
      templateConfig?.regulatoryType !== "RCD"
    ) {
      incompleteDocsList.push(dataKeyToUpdate.templateKey);
    }

    const incompleteDocuments = getIncompleteDependenciesRecursively(
      dataKeyToUpdate.templateKey,
      documents,
      user,
      orgId,
      templatesConfig,
      tasks
    );

    incompleteDocsList = [
      ...new Set([...incompleteDocsList, ...incompleteDocuments]),
    ];
  });

  const hasIncompleteDocuments = incompleteDocsList.length > 0;

  return (
    <div className="flex flex-1 flex-col gap-y-4">
      <PageHeader
        pageTitleComponent={
          <Breadcrumbs aria-label="breadcrumb">
            <Typography>{device.name}</Typography>
            <Typography>Assistant</Typography>
            <Typography>Release Assistant</Typography>
          </Breadcrumbs>
        }
      />
      <div className="flex flex-col gap-y-3 py-2 px-3">
        {hasIncompleteDocuments && (
          <DocWarnings
            incompleteTemplates={incompleteDocsList}
            size="small"
            deviceId={deviceId}
            templatesConfig={templatesConfig}
            tasks={tasks}
            severity="info"
            customTitle="The following documents are used for context on the documents you are going to review. You might want to complete them before you start the process to maximize the quality of our suggestions."
          />
        )}
        <div className="flex flex-col gap-y-4">
          <Alert severity="warning" data-testid="doc-warnings-text">
            By going through the process we will help you adjust necessary
            information and create new versions of the following documents.
          </Alert>
          <div className="flex flex-col gap-y-2">
            {[...new Set(templateKeysToComplete)].map((templateKey) => {
              const templateConfig = templatesConfig[templateKey];

              return (
                <ListCard
                  key={templateKey}
                  text={templateConfig?.defaultTitle || templateKey}
                  StartIcon={NoteAddOutlined}
                  fullWidth
                  justifyContent="flex-start"
                  cursor="default"
                />
              );
            })}
          </div>
        </div>
        <NavigationButtons
          rightButton={{
            onClick: () => handleNext(selectedAssistantProcess),
          }}
          leftButton={{
            hasConfirmationModal: true,
          }}
        />
      </div>
    </div>
  );
};
